import { CompanyContext, CompanyType } from '../company';

export interface UserInfo {
  languageCode: string;
  languageId: number;
  rtl: boolean;
  companyId: number;
  companyName: string;
  companyActive: boolean;
  status: number;
  userId: number;
  firstName: string;
  lastName: string;
  userName: string;
  userPermission: UserPermission;
  displayLanguageCode: string;
  displayLanguageId: number;
  email?: string;
  availableAccounts: AccountContext[];
  productAccess?: GroupProductAccess[];
}

export interface AccountContext {
  name: string;
  administeredBy: string;
  companies: ContextSwitch[];
}

export interface ContextSwitch {
  companyName: string;
  productContextId: number;
  productContextName: string;
  productContextKey: string;
  companyId: number;
  type: CompanyType;
  userId?: number;
  active?: boolean;
}

export interface GroupProductAccess {
  companyId: number;
  groupId: number;
  products: ProductAccess[];
}

export interface ProductAccess {
  companyProductDefinitionId: number;
  productContextId: number;
  key?: string;
  settings: ProductTierSettings;
}

export interface ProductTierSettings {
  hideResultsForNonLeafNodes?: boolean;
  showOrganisationOrgView?: boolean;
  singleManagerAccount?: boolean;
}

/**
 * SingleManagerAccount tiers restrict everyone but system admins: they may only assign themselves
 * as manager and may not hand out permissions, regardless of their role. Mirrors the backend check.
 */
export function isSingleManagerRestricted(
  userInfo: UserInfo | undefined,
  companyContext: CompanyContext | undefined,
): boolean {
  if (!userInfo || !companyContext || userInfo.userPermission?.isAdmin) return false;
  const productContextId = companyContext.productContext?.id;
  return (userInfo.productAccess ?? [])
    .filter((access) => access.companyId === companyContext.id)
    .some((access) =>
      access.products?.some(
        (product) =>
          (productContextId == null || product?.productContextId === productContextId) &&
          product.settings?.singleManagerAccount === true,
      ),
    );
}

export interface UserPermission {
  isAdmin: boolean;
  hasCompanySession: boolean;
  companyId: number;
  groupPermissions: GroupPermission[];

  accessLevels: string[];
  can(
    this: UserPermission,
    permission: string,
    companyId: number,
    productContext: string,
    groupId?: number,
  ): boolean;
  canWithContext(
    this: UserPermission,
    permission: string,
    companyContext: CompanyContext,
    groupId?: number,
  ): boolean;
}

export function canWithContext(
  this: UserPermission,
  permission: string,
  companyContext: CompanyContext,
  groupId?: number,
): boolean {
  return this.can(permission, companyContext.id, companyContext.productContext.key, groupId);
}
export function can(
  this: UserPermission,
  permission: string,
  companyId: number,
  productContext: string,
  groupId?: number,
): boolean {
  function checkPermission(p: ProductContextPermission[]): boolean {
    return p.some(
      (x) =>
        x.permission.startsWith(permission) &&
        (productContext == null || x.productContext === productContext),
    );
  }

  if (this.isAdmin) {
    return true;
  }
  if (this.companyId !== companyId) {
    return false;
  }
  if (groupId) {
    return this.groupPermissions
      .filter(x => groupId < 0 || x.groupId === groupId)
      .some(gp => checkPermission(gp.permissions));
  } else if (this.hasCompanySession) {
    return this.groupPermissions
      .some(gp => checkPermission(gp.permissions))
  }
  return false;
}

/**
 * Like `can`, but only counts permissions from a session directly on the group — not
 * inherited from a parent group's session, and not from a company-wide session.
 */
export function hasDirectPermission(
  userPermission: UserPermission | undefined,
  permission: string,
  companyContext: CompanyContext,
  groupId: number | null | undefined,
): boolean {
  if (!userPermission) return false;
  if (userPermission.isAdmin) return true;
  if (groupId == null || userPermission.companyId !== companyContext?.id) return false;
  const productContext = companyContext.productContext?.key;
  return userPermission.groupPermissions.some(
    (gp) =>
      gp.groupId === groupId &&
      (gp.directPermissions ?? []).some(
        (p) =>
          p.permission.startsWith(permission) &&
          (productContext == null || p.productContext === productContext),
      ),
  );
}

export interface GroupPermission {
  groupId: number;
  groupName: string;
  relation: string;
  permissions: ProductContextPermission[];
  /** Permissions from a session directly on this group (not inherited, not company-wide). */
  directPermissions?: ProductContextPermission[];
  hasChildren?: boolean;
  children?: GroupPermission[];
}

export interface ProductContextPermission {
  productContext: string;
  permission: string;
}
